<template>
  <modal
    name="drive-groups"
    height="auto"
    width="680px"
    :scrollable="true"
    :adaptive="true"
    class="modal-dialog"
    @closed="$emit('close')"
  >
    <div class="modal-content o-common-modal-content">
      <div class="modal-header text-center">
        <button type="button" class="close" @click="closeModal">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
        <h4 class="modal-title">
          <i class="fa fa-users" aria-hidden="true"></i>
          User Groups
        </h4>
      </div>
      <div class="modal-body" v-loading="loading">
        <!-- Group list / detail toggle -->
        <div v-if="!selectedGroup">
          <!-- Create new group -->
          <div class="drive-groups-create">
            <input
              type="text"
              class="form-control"
              placeholder="New group name"
              v-model="newGroupName"
              @keyup.enter="createGroup"
            />
            <input
              type="text"
              class="form-control"
              placeholder="Description (optional)"
              v-model="newGroupDesc"
              @keyup.enter="createGroup"
            />
            <button class="btn btn-success btn-sm" :disabled="!newGroupName" @click="createGroup">
              <i class="fa fa-plus" aria-hidden="true"></i> Create
            </button>
          </div>

          <!-- Groups list -->
          <div class="drive-groups-list">
            <div
              v-for="group in groups"
              :key="group.id"
              class="drive-groups-item"
            >
              <div class="drive-groups-item-info" @click="openGroup(group)">
                <strong>{{ group.name }}</strong>
                <span class="text-muted" v-if="group.description"> - {{ group.description }}</span>
                <span class="badge" style="margin-left: 8px;">{{ group.members_count || 0 }} members</span>
              </div>
              <div class="drive-groups-item-actions">
                <el-tooltip content="Edit" placement="top" effect="dark">
                  <a href="javascript: void(0)" @click="promptEditGroup(group)">
                    <i class="fa fa-pencil" aria-hidden="true"></i>
                  </a>
                </el-tooltip>
                <el-tooltip content="Delete" placement="top" effect="dark">
                  <a href="javascript: void(0)" class="drive-groups-delete" @click="promptDeleteGroup(group)">
                    <i class="fa fa-trash-o" aria-hidden="true"></i>
                  </a>
                </el-tooltip>
              </div>
            </div>
            <div v-if="groups.length === 0" class="drive-groups-empty">
              No groups created yet.
            </div>
          </div>
        </div>

        <!-- Group detail: members -->
        <div v-if="selectedGroup">
          <div class="drive-groups-detail-header">
            <a href="javascript: void(0)" @click="selectedGroup = null; members = []">
              <i class="fa fa-arrow-left" aria-hidden="true"></i> Back to Groups
            </a>
            <h5 style="margin: 10px 0 5px;">
              <i class="fa fa-users" aria-hidden="true"></i>
              {{ selectedGroup.name }}
            </h5>
            <p v-if="selectedGroup.description" class="text-muted" style="font-size: 13px;">{{ selectedGroup.description }}</p>
          </div>

          <!-- Add member -->
          <div class="drive-groups-create">
            <div class="drive-groups-search-wrap">
              <input
                type="text"
                class="form-control"
                placeholder="Search employees to add..."
                v-model="memberSearch"
                @input="searchMembers"
              />
              <div v-if="memberResults.length > 0" class="drive-groups-dropdown">
                <div
                  v-for="emp in memberResults"
                  :key="emp.id"
                  class="drive-groups-dropdown-item"
                  @click="addMember(emp)"
                >
                  {{ emp.first_name }} {{ emp.last_name }}
                  <span v-if="emp.email" class="text-muted">&lt;{{ emp.email }}&gt;</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Members list -->
          <div class="drive-groups-list">
            <div
              v-for="member in members"
              :key="member.id"
              class="drive-groups-item"
            >
              <div class="drive-groups-item-info">
                <i class="fa fa-user" aria-hidden="true"></i>
                <strong>{{ member.first_name }} {{ member.last_name }}</strong>
                <span v-if="member.email" class="text-muted drive-groups-member-email">&lt;{{ member.email }}&gt;</span>
              </div>
              <a href="javascript: void(0)" class="drive-groups-remove-btn" @click="removeMember(member)" title="Remove from group">
                <i class="fa fa-times" aria-hidden="true"></i>
              </a>
            </div>
            <div v-if="members.length === 0" class="drive-groups-empty">
              No members in this group.
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer text-center">
        <a
          href="javascript: void(0)"
          class="c-solid-btn"
          @click="closeModal"
        >
          Close
        </a>
      </div>
    </div>
  </modal>
</template>

<script>
import service from "../../services.js";

export default {
  name: "DriveGroupManager",
  props: {
    apiHeaders: { type: Object, required: true },
  },
  data: function () {
    return {
      loading: false,
      groups: [],
      newGroupName: "",
      newGroupDesc: "",

      // Detail view
      selectedGroup: null,
      members: [],
      memberSearch: "",
      memberResults: [],
      memberSearchTimer: null,
    };
  },
  mounted: function () {
    this.$modal.show("drive-groups");
    this.loadGroups();
  },
  methods: {
    loadGroups: function () {
      var self = this;
      this.loading = true;
      service
        .get("drive/groups", this.apiHeaders)
        .then(function (res) {
          self.loading = false;
          if (res.data.status_code === "1") {
            self.groups = res.data.result || [];
          }
        })
        .catch(function () {
          self.loading = false;
        });
    },

    createGroup: function () {
      if (!this.newGroupName) return;
      var self = this;
      this.loading = true;
      service
        .post("drive/groups", { name: this.newGroupName, description: this.newGroupDesc }, this.apiHeaders)
        .then(function (res) {
          self.loading = false;
          if (res.data.status_code === "1") {
            self.$message({ type: "success", message: 'Group "' + self.newGroupName + '" created' });
            self.newGroupName = "";
            self.newGroupDesc = "";
            self.loadGroups();
          } else {
            self.$message({ type: "error", message: res.data.status_message || "Failed to create group" });
          }
        })
        .catch(function () {
          self.loading = false;
          self.$message({ type: "error", message: "Failed to create group" });
        });
    },

    promptEditGroup: function (group) {
      var self = this;
      this.$prompt("Enter new name for group", "Edit Group", {
        confirmButtonText: "Save",
        cancelButtonText: "Cancel",
        inputValue: group.name,
        inputPattern: /^.{1,255}$/,
        inputErrorMessage: "Name is required (max 255 characters)",
      })
        .then(function (result) {
          if (result.value && result.value !== group.name) {
            self.loading = true;
            service
              .put("drive/groups/" + encodeURIComponent(group.id), { name: result.value }, self.apiHeaders)
              .then(function (res) {
                self.loading = false;
                if (res.data.status_code === "1") {
                  self.$message({ type: "success", message: "Group renamed" });
                  self.loadGroups();
                } else {
                  self.$message({ type: "error", message: res.data.status_message || "Rename failed" });
                }
              })
              .catch(function () {
                self.loading = false;
                self.$message({ type: "error", message: "Rename failed" });
              });
          }
        })
        .catch(function () {});
    },

    promptDeleteGroup: function (group) {
      var self = this;
      this.$confirm(
        'Delete group "' + group.name + '"? This will also remove all permissions assigned to this group.',
        "Delete Group",
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      )
        .then(function () {
          self.loading = true;
          service
            .del("drive/groups/" + encodeURIComponent(group.id), self.apiHeaders)
            .then(function (res) {
              self.loading = false;
              if (res.data.status_code === "1") {
                self.$message({ type: "success", message: "Group deleted" });
                self.loadGroups();
              } else {
                self.$message({ type: "error", message: res.data.status_message || "Delete failed" });
              }
            })
            .catch(function () {
              self.loading = false;
              self.$message({ type: "error", message: "Delete failed" });
            });
        })
        .catch(function () {});
    },

    openGroup: function (group) {
      this.selectedGroup = group;
      this.loadMembers();
    },

    loadMembers: function () {
      var self = this;
      this.loading = true;
      service
        .get("drive/groups/" + encodeURIComponent(this.selectedGroup.id) + "/members", this.apiHeaders)
        .then(function (res) {
          self.loading = false;
          if (res.data.status_code === "1") {
            self.members = res.data.result || [];
          }
        })
        .catch(function () {
          self.loading = false;
        });
    },

    searchMembers: function () {
      var self = this;
      if (this.memberSearchTimer) {
        clearTimeout(this.memberSearchTimer);
      }
      if (!this.memberSearch || this.memberSearch.length < 2) {
        this.memberResults = [];
        return;
      }
      this.memberSearchTimer = setTimeout(function () {
        var headers = Object.assign({}, self.apiHeaders, {
          searchEmployee: self.memberSearch,
        });
        service
          .get("employee/searchEmployee", headers)
          .then(function (res) {
            self.memberResults = (res.data && res.data.result) || [];
          })
          .catch(function () {
            self.memberResults = [];
          });
      }, 300);
    },

    addMember: function (emp) {
      var self = this;
      this.memberResults = [];
      this.memberSearch = "";
      this.loading = true;
      service
        .post(
          "drive/groups/" + encodeURIComponent(this.selectedGroup.id) + "/members",
          { employee_id: emp.id },
          this.apiHeaders
        )
        .then(function (res) {
          self.loading = false;
          if (res.data.status_code === "1") {
            self.$message({ type: "success", message: emp.first_name + " " + emp.last_name + " added" });
            self.loadMembers();
            self.loadGroups();
          } else {
            self.$message({ type: "error", message: res.data.status_message || "Failed to add member" });
          }
        })
        .catch(function () {
          self.loading = false;
          self.$message({ type: "error", message: "Failed to add member" });
        });
    },

    removeMember: function (member) {
      var self = this;
      this.$confirm(
        "Remove " + member.first_name + " " + member.last_name + " from this group?",
        "Remove Member",
        {
          confirmButtonText: "Remove",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      )
        .then(function () {
          self.loading = true;
          service
            .del(
              "drive/groups/" + encodeURIComponent(self.selectedGroup.id) + "/members/" + encodeURIComponent(member.id),
              self.apiHeaders
            )
            .then(function (res) {
              self.loading = false;
              if (res.data.status_code === "1") {
                self.$message({ type: "success", message: "Member removed" });
                self.loadMembers();
                self.loadGroups();
              } else {
                self.$message({ type: "error", message: res.data.status_message || "Remove failed" });
              }
            })
            .catch(function () {
              self.loading = false;
              self.$message({ type: "error", message: "Remove failed" });
            });
        })
        .catch(function () {});
    },

    closeModal: function () {
      this.$modal.hide("drive-groups");
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.drive-groups-create {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}
.drive-groups-create .form-control {
  flex: 1;
  min-width: 120px;
}
.drive-groups-search-wrap {
  flex: 1;
  position: relative;
  min-width: 200px;
}
.drive-groups-search-wrap .form-control {
  width: 100%;
}
.drive-groups-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
.drive-groups-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
}
.drive-groups-dropdown-item:hover {
  background: #f5f7fa;
}
.drive-groups-list {
  border: 1px solid #eee;
  border-radius: 4px;
  max-height: 350px;
  overflow-y: auto;
}
.drive-groups-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #f5f5f5;
}
.drive-groups-item:last-child {
  border-bottom: none;
}
.drive-groups-item-info {
  flex: 1;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}
.drive-groups-item-actions {
  display: flex;
  gap: 10px;
  margin-left: 10px;
}
.drive-groups-item-actions a {
  color: #666;
  font-size: 14px;
}
.drive-groups-item-actions a:hover {
  color: #337ab7;
}
.drive-groups-delete:hover {
  color: #c0392b !important;
}
.drive-groups-empty {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 13px;
}
.drive-groups-member-email {
  margin-left: 6px;
  font-size: 12px;
}
.drive-groups-remove-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #999;
  font-size: 14px;
  flex-shrink: 0;
  margin-left: 10px;
}
.drive-groups-remove-btn:hover {
  background: #fde8e8;
  color: #c0392b;
}
.drive-groups-detail-header {
  margin-bottom: 15px;
}
.drive-groups-detail-header a {
  color: #337ab7;
  font-size: 13px;
}
</style>
